import { useEffect, useRef, useState } from "react";

import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

/** Long enough to read as movement, short enough not to hold a stale figure on screen. */
const DEFAULT_DURATION_MS = 700;

/** Ease-out cubic: fast off the mark, settling into the final value. */
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Counts from the previously shown amount to the new one, for display only.
 *
 * Returns `null` while at rest — the caller then renders the authoritative decimal **string** untouched,
 * which is the whole point: the interpolated `Number` never becomes the value, it only fills the frames in
 * between (`decimal.ts`: "only display formatting leaves the string domain"). The last frame is always the
 * real target, so the animation can never leave a rounded float on screen.
 *
 * No animation on first paint (there is nothing to count from), when the value did not change, when the user
 * prefers reduced motion, or when either side is not a finite number.
 */
export function useAnimatedAmount(
  value: string | number,
  enabled: boolean,
  durationMs: number = DEFAULT_DURATION_MS,
): number | null {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [frame, setFrame] = useState<number | null>(null);
  const [seenValue, setSeenValue] = useState(value);
  const previousRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  // Adjusting state during render (the React-documented pattern for "props changed"): a new value must
  // never render through the previous animation's frame. Doing it in an effect would paint a stale figure
  // for one frame and cascade a second render.
  if (value !== seenValue) {
    setSeenValue(value);
    setFrame(null);
  }

  useEffect(() => {
    const target = typeof value === "string" ? Number(value) : value;
    const from = previousRef.current;
    previousRef.current = Number.isFinite(target) ? target : null;

    const skip =
      !enabled || prefersReducedMotion || from === null || !Number.isFinite(target) || from === target;
    if (skip) return;

    const startedAt = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - startedAt) / durationMs, 1);
      if (progress >= 1) {
        // Hand the last frame back to the caller's exact string.
        setFrame(null);
        rafRef.current = null;
        return;
      }
      setFrame(from + (target - from) * easeOut(progress));
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [value, enabled, prefersReducedMotion, durationMs]);

  return frame;
}
